import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  makeStyles,
  Link,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 400,
  },
  field: {
    marginTop: theme.spacing(1),
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  successIcon: {
    color: theme.palette.success.main,
    fontSize: 48,
  },
  prLink: {
    marginTop: theme.spacing(1),
  },
}));

export interface PrSubmissionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (title: string, description: string) => Promise<void>;
  entityName: string;
  prUrl?: string;
  isSubmitting?: boolean;
}

export const PrSubmissionDialog = ({
  open,
  onClose,
  onSubmit,
  entityName,
  prUrl,
  isSubmitting = false,
}: PrSubmissionDialogProps) => {
  const classes = useStyles();
  const [title, setTitle] = useState(`Update catalog-info.yaml for ${entityName}`);
  const [description, setDescription] = useState(
    `This PR updates the catalog-info.yaml file for the ${entityName} entity.\n\nChanges made via Backstage YAML Editor.`,
  );

  const handleSubmit = async () => {
    await onSubmit(title, description);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      // Reset form after close
      setTimeout(() => {
        setTitle(`Update catalog-info.yaml for ${entityName}`);
        setDescription(
          `This PR updates the catalog-info.yaml file for the ${entityName} entity.\n\nChanges made via Backstage YAML Editor.`,
        );
      }, 300);
    }
  };

  if (prUrl) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Pull Request Created</DialogTitle>
        <DialogContent>
          <div className={classes.successContainer}>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography variant="h6">Success!</Typography>
            <Typography variant="body1" color="textSecondary" align="center">
              Your pull request has been created successfully.
            </Typography>
            <Link
              href={prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.prLink}
            >
              View Pull Request →
            </Link>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Pull Request</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <Typography variant="body2" color="textSecondary">
            Your changes will be submitted as a pull request to the repository.
            Please provide a title and description for the PR.
          </Typography>
          <TextField
            className={classes.field}
            label="PR Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            fullWidth
            variant="outlined"
            disabled={isSubmitting}
            required
          />
          <TextField
            className={classes.field}
            label="PR Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            disabled={isSubmitting}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isSubmitting || !title.trim()}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Creating PR...' : 'Create Pull Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

